import React, { useState, useEffect, useRef } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useRipple } from '../hooks/useRipple';
import AdUnit from './AdUnit';
import './styles/clickEffects.css';

export default function AppNav({
  activePage = 'home',
  priceMode = 'student',
  onModeToggle,
  onNavigate,
  isMobile = false,
  user = null,
  onUserChange,
  notifications = [],
  unseenCount = 0,
  onMarkAllSeen,
  onNotificationClick,
  formatRelative,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const bellRef     = useRef(null);
  const mobileBellRef = useRef(null);
  const [currency, setCurrency] = useState({ code: 'NAG', symbol: '₦', label: 'NAG' });
  const [locating, setLocating] = useState(false);
  const [locationLabel, setLocationLabel] = useState(null);
  const [showSignOut, setShowSignOut] = useState(false);
  const isLive = priceMode === 'live';
  const triggerRipple = useRipple();

  // ── Geolocation ────────────────────────────────────────────
  useEffect(() => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${coords.latitude}&lon=${coords.longitude}&format=json`
          );
          const data = await res.json();
          const addr = data?.address || {};
          const countryCode = addr.country_code?.toUpperCase();
          const city = addr.city || addr.town || addr.village || addr.suburb || addr.county || addr.district || null;
          const state = addr.state || addr.region || null;
          const cleanState = state?.replace(/\s+State$/i, '') || null;
          if (city && cleanState) setLocationLabel(`${city}, ${cleanState}`);
          else if (cleanState) setLocationLabel(cleanState);
          else if (city) setLocationLabel(city);
          const CURRENCY_MAP = {
            NG: { code: 'NGN', symbol: '₦', label: 'NAG' },
            US: { code: 'USD', symbol: '$', label: 'NAG' },
            GB: { code: 'GBP', symbol: '£', label: 'NAG' },
            GH: { code: 'GHS', symbol: 'GH₵', label: 'NAG' },
            KE: { code: 'KES', symbol: 'KSh', label: 'NAG' },
            ZA: { code: 'ZAR', symbol: 'R', label: 'NAG' },
            EU: { code: 'EUR', symbol: '€', label: 'NAG' },
          };
          if (countryCode && CURRENCY_MAP[countryCode]) setCurrency(CURRENCY_MAP[countryCode]);
        } catch (_) {}
        finally { setLocating(false); }
      },
      () => setLocating(false),
      { timeout: 8000 }
    );
  }, []);

  // ── Google sign-in ─────────────────────────────────────────
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        const profile = await res.json();
        if (onUserChange) {
          onUserChange({
            name: profile.name,
            avatarUrl: profile.picture,
            balance: 0,
          });
        }
      } catch (err) {
        console.error('Failed to fetch Google profile:', err);
      }
    },
    onError: () => console.error('Google sign-in failed'),
  });

  const handleSignOut = () => {
    if (onUserChange) onUserChange(null);
    setShowSignOut(false);
  };

  const isGuest = !user;
  const displayName = user?.name || 'Guest';
  const displayBal = user?.balance != null ? user.balance.toLocaleString() : '';
  const initials = displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const hasAvatar = Boolean(user?.avatarUrl);

  const navLinks = [
    { label: 'Markets', page: 'home' },
    { label: 'Predict', page: 'predict' },
    { label: 'Prices',  page: 'prices' },
    { label: 'News',    page: 'news' },
    { label: 'About',   page: 'about',  small: true },
    { label: 'Terms',   page: 'terms',  small: true },
  ];

  const go = (page) => { if (onNavigate) onNavigate(page); setMenuOpen(false); setBellOpen(false); };

  // Close bell panel on outside click (desktop)
  useEffect(() => {
    if (!bellOpen) return;
    const handler = (e) => {
      const clickedDesktop = bellRef.current && bellRef.current.contains(e.target);
      const clickedMobile  = mobileBellRef.current && mobileBellRef.current.contains(e.target);
      if (!clickedDesktop && !clickedMobile) setBellOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [bellOpen]);

  // ── Shared notification dropdown content ───────────────────
  // On mobile: use position:fixed so it escapes the nav's stacking context
  // and can't be clipped. Anchored 16px from each side edge, just below nav.
  const NotificationPanel = ({ alignRight = true }) => (
    <div style={isMobile ? {
      position: 'fixed',
      top: 'clamp(52px, 14vw, 64px)',  // matches mobile nav height
      left: 12,
      right: 12,
      maxHeight: '70vh',
      background: '#fff',
      borderRadius: 12,
      boxShadow: '0 8px 40px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.10)',
      border: '1px solid rgba(10,46,20,0.1)',
      overflow: 'hidden',
      zIndex: 10000,
      animation: 'bell-panel-in 0.2s cubic-bezier(.22,1,.36,1) both',
    } : {
      position: 'absolute',
      top: 'calc(100% + 10px)',
      right: alignRight ? -8 : undefined,
      left: alignRight ? undefined : -8,
      width: 340,
      maxWidth: 380,
      maxHeight: 420,
      background: '#fff',
      borderRadius: 12,
      boxShadow: '0 8px 40px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)',
      border: '1px solid rgba(10,46,20,0.1)',
      overflow: 'hidden',
      zIndex: 9999,
      animation: 'bell-panel-in 0.2s cubic-bezier(.22,1,.36,1) both',
    }}>
      <style>{`
        @keyframes bell-pop { from{transform:scale(0)} to{transform:scale(1)} }
        @keyframes bell-panel-in { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
        .notif-item:hover { background: rgba(10,46,20,0.04) !important; }
      `}</style>
      {/* Header */}
      <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid rgba(10,46,20,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#0a2e14', fontFamily: 'Space Grotesk, sans-serif' }}>Notifications</span>
        {unseenCount > 0 && (
          <span style={{ fontSize: 10, fontWeight: 600, color: '#64748b', cursor: 'pointer' }} onClick={onMarkAllSeen}>Mark all read</span>
        )}
      </div>
      {/* List */}
      <div style={{ overflowY: 'auto', maxHeight: isMobile ? 'calc(70vh - 56px)' : 340 }}>
        {notifications.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>🔔</div>
            No notifications yet
          </div>
        ) : notifications.map(n => (
          <div
            key={n.id}
            className="notif-item"
            onClick={() => {
              if (onNotificationClick) onNotificationClick(n);
              setBellOpen(false);
            }}
            style={{
              padding: '12px 16px', cursor: 'pointer',
              borderBottom: '1px solid rgba(10,46,20,0.05)',
              background: 'transparent',
              display: 'flex', gap: 10, alignItems: 'flex-start',
              transition: 'background 0.15s',
              position: 'relative',
            }}
          >
            <div style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: n.type === 'blog' ? 'rgba(10,46,20,0.08)' : 'rgba(212,160,23,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
            }}>
              {n.type === 'blog' ? '📝' : '📰'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0f172a', lineHeight: 1.35, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {n.title}
              </div>
              {n.body && (
                <div style={{ fontSize: 11.5, color: '#475569', lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                  {n.body}
                </div>
              )}
              <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 4, fontFamily: 'Space Grotesk, sans-serif', fontWeight: 500 }}>
                {n.type === 'blog' ? '✦ New Blog Post' : '📡 News'} · {formatRelative ? formatRelative(n.timestamp) : 'Just now'}
              </div>
            </div>
            {!n.seen && (
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', flexShrink: 0, marginTop: 4 }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // ── Bell button (reusable) ─────────────────────────────────
  const BellButton = () => (
    <button
      onClick={() => { setBellOpen(o => !o); if (!bellOpen && onMarkAllSeen) onMarkAllSeen(); }}
      style={{
        position: 'relative', background: 'transparent', border: 'none',
        cursor: 'pointer', padding: isMobile ? 'clamp(4px,1.5vw,6px)' : 4,
        display: 'flex', alignItems: 'center', flexShrink: 0,
      }}
      title="Notifications"
    >
      <svg
        width={isMobile ? 'clamp(18px,5.5vw,22px)' : 20}
        height={isMobile ? 'clamp(18px,5.5vw,22px)' : 20}
        viewBox="0 0 24 24" fill="none" stroke="white"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>
      {unseenCount > 0 && (
        <span style={{
          position: 'absolute', top: -1, right: -1,
          minWidth: 16, height: 16, borderRadius: 8,
          background: '#ef4444', border: '2px solid var(--green-900)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 9, fontWeight: 800, color: '#fff',
          fontFamily: 'Space Grotesk, sans-serif',
          padding: unseenCount > 9 ? '0 3px' : 0,
          lineHeight: 1,
          animation: 'bell-pop 0.4s cubic-bezier(.34,1.56,.64,1) both',
        }}>
          {unseenCount > 99 ? '99+' : unseenCount}
        </span>
      )}
    </button>
  );

  // ── UserChip ───────────────────────────────────────────────
  const UserChip = ({ avatarOnly = false }) => {
    if (isGuest) {
      if (avatarOnly) {
        return (
          <button
            onClick={() => googleLogin()}
            onPointerDown={triggerRipple}
            className="ripple-host"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32,
              background: '#fff',
              border: 'none',
              borderRadius: '50%',
              cursor: 'pointer',
              boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
              flexShrink: 0,
              padding: 0,
            }}
            title="Sign in with Google"
          >
            <svg width="16" height="16" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.08 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-3.59-13.46-8.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              <path fill="none" d="M0 0h48v48H0z"/>
            </svg>
          </button>
        );
      }

      return (
        <button
          onClick={() => googleLogin()}
          onPointerDown={triggerRipple}
          className="ripple-host"
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: '#fff', border: 'none', borderRadius: 20,
            padding: '6px 14px', cursor: 'pointer',
            fontSize: 13, fontWeight: 700, color: '#1a1a1a',
            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
            flexShrink: 0, whiteSpace: 'nowrap',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.08 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-3.59-13.46-8.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            <path fill="none" d="M0 0h48v48H0z"/>
          </svg>
          Sign in with Google
        </button>
      );
    }

    if (avatarOnly) {
      return (
        <div style={{ position: 'relative' }}>
          <div onClick={() => setShowSignOut(o => !o)} style={{ cursor: 'pointer', flexShrink: 0 }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: hasAvatar ? 'transparent' : 'var(--gold)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, color: 'var(--green-900)',
              overflow: 'hidden', border: '2px solid var(--gold)', flexShrink: 0,
            }}>
              {hasAvatar
                ? <img src={user.avatarUrl} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : initials}
            </div>
          </div>
          {showSignOut && (
            <div style={{
              position: 'absolute', top: '110%', right: 0,
              background: '#fff', borderRadius: 10,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              padding: '4px 0', minWidth: 140, zIndex: 9999,
            }}>
              <div style={{ padding: '8px 16px', fontSize: 12, color: '#888', borderBottom: '1px solid #f0f0f0' }}>{user.name}</div>
              <button onClick={handleSignOut} style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                Sign out
              </button>
            </div>
          )}
        </div>
      );
    }

    return (
      <div style={{ position: 'relative' }}>
        <div
          onClick={() => setShowSignOut(o => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 10px', borderRadius: 20, background: 'rgba(255,255,255,0.08)' }}
        >
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: hasAvatar ? 'transparent' : 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--green-900)', overflow: 'hidden', border: '2px solid var(--gold)', flexShrink: 0 }}>
            {hasAvatar ? <img src={user.avatarUrl} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#fff', whiteSpace: 'nowrap' }}>{displayName}</div>
            {user?.balance > 0 && <div style={{ fontSize: 11, color: 'var(--gold-light)', fontWeight: 600 }}>₦{displayBal}</div>}
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 3, marginTop: 1 }}>
              {locating ? <LocatingDots /> : locationLabel ? (
                <>
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2a7 7 0 0 1 7 7c0 5.25-7 13-7 13S5 14.25 5 9a7 7 0 0 1 7-7z"/>
                    <circle cx="12" cy="9" r="2.5"/>
                  </svg>
                  {locationLabel}
                </>
              ) : null}
            </div>
          </div>
        </div>
        {showSignOut && (
          <div style={{ position: 'absolute', top: '110%', right: 0, background: '#fff', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', padding: '4px 0', minWidth: 140, zIndex: 9999 }}>
            <div style={{ padding: '8px 16px', fontSize: 12, color: '#888', borderBottom: '1px solid #f0f0f0' }}>{user.name}</div>
            <button onClick={handleSignOut} style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Sign out
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000 }}>
      <nav style={{
        position: 'relative',
        height: isMobile ? 'clamp(52px, 14vw, 64px)' : 80,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: isMobile ? '0 clamp(0.5rem, 3vw, 1.25rem)' : '0 2rem',
      }}>
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0,
          background: 'var(--green-900)',
          clipPath: isMobile ? 'none' : 'polygon(0% 0%, 100% 0%, 100% 100%, 78% 100%, 73% 35%, 27% 35%, 22% 100%, 0% 100%)',
          WebkitClipPath: isMobile ? 'none' : 'polygon(0% 0%, 100% 0%, 100% 100%, 78% 100%, 73% 35%, 27% 35%, 22% 100%, 0% 100%)',
          filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.45))',
        }} />

        {/* Logo */}
        <button onClick={() => go('home')} style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 'clamp(4px, 2vw, 10px)' : 10, background: 'transparent', border: 'none', cursor: 'pointer', flexShrink: 0, position: 'relative', zIndex: 1, padding: 0 }}>
          <div style={{ width: isMobile ? 'clamp(26px, 8vw, 36px)' : 36, height: isMobile ? 'clamp(26px, 8vw, 36px)' : 36, borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
            <img src="/android-chrome-192x192.png" alt="NaijaAgro Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <span style={{ fontFamily: 'var(--font-head)', fontSize: isMobile ? 'clamp(11px, 3.5vw, 14px)' : 16, fontWeight: 700, color: '#fff', letterSpacing: '-0.3px', whiteSpace: 'nowrap' }}>
            NaijaAgro Predict
          </span>
        </button>

        {/* Desktop: centered nav links + toggle */}
        {!isMobile && (
          <ul style={{ position: 'absolute', left: '50%', top: '65%', transform: 'translate(-50%, -50%)', display: 'flex', gap: '0.35rem', listStyle: 'none', margin: 0, padding: 0, zIndex: 1 }}>
            {navLinks.map(({ label, page }) => {
              const isActive = activePage === page;
              return (
                <li key={label}>
                  <button
                    onClick={() => go(page)}
                    style={{ display: 'inline-flex', alignItems: 'center', color: isActive ? 'var(--green-900)' : '#fff', fontSize: 13, fontWeight: 600, padding: '7px 18px', borderRadius: 30, border: 'none', background: isActive ? 'var(--gold)' : 'black', transition: 'all 0.2s', whiteSpace: 'nowrap', cursor: 'pointer' }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(0,0,0,0.8)'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'black'; }}
                  >
                    {label}
                  </button>
                </li>
              );
            })}
            <li key="_toggle" style={{ listStyle: 'none', marginLeft: 6 }}>
              <ModeToggle isLive={isLive} onToggle={onModeToggle} />
            </li>
          </ul>
        )}

        {/* Desktop: right side — bell + user chip */}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 1 }}>
            <div ref={bellRef} style={{ position: 'relative' }}>
              <BellButton />
              {bellOpen && <NotificationPanel alignRight={true} />}
            </div>
            <UserChip avatarOnly={false} />
          </div>
        )}

        {/* Mobile: right side — bell + toggle + avatar + hamburger */}
        {isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(4px, 1.8vw, 8px)', position: 'relative', zIndex: 1 }}>
            {/* Bell button only — panel rendered outside nav below to avoid clipping */}
            <div ref={mobileBellRef} style={{ position: 'relative' }}>
              <BellButton />
            </div>

            <ModeToggle isLive={isLive} onToggle={onModeToggle} compact />
            <UserChip avatarOnly />
            <button
              onClick={() => setMenuOpen(o => !o)}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 'clamp(4px, 1.5vw, 8px)', display: 'flex', alignItems: 'center' }}
            >
              <svg width="clamp(18px, 5.5vw, 24px)" height="clamp(18px, 5.5vw, 24px)" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                {menuOpen
                  ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
                  : <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>
                }
              </svg>
            </button>
          </div>
        )}
      </nav>

      {/* Mobile notification panel — outside nav so it's never clipped.
          position:fixed, edge-to-edge, just below the nav bar. */}
      {isMobile && bellOpen && (
        <>
          {/* Semi-transparent backdrop — tap anywhere outside to close */}
          <div
            onClick={() => setBellOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.25)' }}
          />
          <NotificationPanel alignRight={true} />
        </>
      )}

      {/* Mobile dropdown */}
      {isMobile && menuOpen && (
        <div style={{ background: 'var(--green-900)', padding: '0.75rem 1.25rem 1.25rem', display: 'flex', flexDirection: 'column', gap: 4, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          {navLinks.map(({ label, page, small }) => (
            <button
              key={label}
              onClick={() => go(page)}
              style={{ color: activePage === page ? 'var(--green-900)' : '#fff', fontSize: small ? 13 : 15, fontWeight: 600, padding: small ? '10px 0' : '12px 0', background: activePage === page ? 'var(--gold)' : 'black', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer', textAlign: 'center' }}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LocatingDots() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(255,255,255,0.4)', display: 'inline-block', animation: 'navPulse 1.2s ease-in-out infinite', animationDelay: `${i * 0.2}s` }} />
      ))}
      <style>{`@keyframes navPulse { 0%, 100% { opacity: 0.2; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1.2); } }`}</style>
    </span>
  );
}

function ModeToggle({ isLive, onToggle, compact = false }) {
  const sz = compact
    ? { pill: 'clamp(56px,16vw,68px)', h: 'clamp(26px,7.5vw,30px)', fs: 'clamp(9px,2.5vw,11px)' }
    : { pill: '110px', h: '32px', fs: '11px' };
  return (
    <div
      onClick={onToggle}
      title={isLive ? 'Switch to Demo mode' : 'Switch to Live mode'}
      style={{
        position: 'relative', display: 'flex', alignItems: 'center',
        width: sz.pill, height: sz.h,
        background: 'rgba(0,0,0,0.35)',
        border: '1.5px solid rgba(255,255,255,0.15)',
        borderRadius: 999, cursor: 'pointer', flexShrink: 0,
        padding: 3, userSelect: 'none', transition: 'border-color 0.25s',
      }}
    >
      <span style={{
        position: 'absolute', top: 3, bottom: 3, width: 'calc(50% - 3px)',
        left: isLive ? 'calc(50%)' : 3, borderRadius: 999,
        background: isLive ? 'linear-gradient(90deg,#166534,#15803d)' : 'rgba(255,255,255,0.18)',
        boxShadow: isLive ? '0 0 10px rgba(34,197,94,0.4)' : 'none',
        transition: 'left 0.28s cubic-bezier(.22,.68,0,1.2), background 0.28s, box-shadow 0.28s',
      }} />
      <span style={{ position: 'relative', zIndex: 1, flex: 1, textAlign: 'center', fontSize: sz.fs, fontWeight: 700, fontFamily: 'var(--font-head)', letterSpacing: '0.04em', color: !isLive ? '#fff' : 'rgba(255,255,255,0.4)', transition: 'color 0.25s' }}>Demo</span>
      <span style={{ position: 'relative', zIndex: 1, flex: 1, textAlign: 'center', fontSize: sz.fs, fontWeight: 700, fontFamily: 'var(--font-head)', letterSpacing: '0.04em', color: isLive ? '#fff' : 'rgba(255,255,255,0.4)', transition: 'color 0.25s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
        {isLive && <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ade80', boxShadow: '0 0 5px #4ade80', display: 'inline-block', flexShrink: 0 }} />}
        Live
      </span>
    </div>
  );
}